import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../PageMeta';

/* Rotating hero word: crossfades between phrases while the container width
   eases to the active word, so the rest of the sentence glides rather than
   jumping. Stays static when the user prefers reduced motion. */
const SWAP_WORDS = ['before', 'not after'];

function RotatingWord() {
  const [idx, setIdx] = useState(0);
  const [width, setWidth] = useState(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = setInterval(() => setIdx(i => (i + 1) % SWAP_WORDS.length), 3200);
    return () => clearInterval(timer);
  }, []);

  useLayoutEffect(() => {
    const el = itemRefs.current[idx];
    if (el) setWidth(el.offsetWidth);
  }, [idx]);

  return (
    <span className="swap-word" style={width ? { width } : undefined}>
      {SWAP_WORDS.map((w, i) => (
        <em
          key={w}
          ref={el => { itemRefs.current[i] = el; }}
          className={`swap-item ${i === idx ? 'swap-item-in' : 'swap-item-out'}`}
          aria-hidden={i !== idx}
        >
          {w}
        </em>
      ))}
    </span>
  );
}

/* Small inline icon set (Lucide-style strokes) so the page needs no icon lib. */
const I = {
  arrow: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>,
  flag: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></svg>,
  map: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21" /><line x1="8" y1="3" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="21" /></svg>,
  dollars: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  chat: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
  clock: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
  shield: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  home: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>,
  gavel: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 13l-7.5 7.5a2.12 2.12 0 0 1-3-3L11 10" /><path d="M16 16l6 6" /><path d="M8 8l6-6 8 8-6 6z" /></svg>,
  trend: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
  upload: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
  scan: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
  report: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>,
};

/* Miniature plan-of-subdivision schematic used in the hero report mock. */
function MiniPlan() {
  return (
    <svg viewBox="0 0 150 96" className="hero-mock-plan-svg" aria-label="Plan of subdivision with the lot circled">
      <rect x="1" y="1" width="148" height="94" rx="3" fill="#FDFCF9" stroke="#DDD5C5" />
      <g stroke="#6E6E73" strokeWidth="1.2" fill="none">
        <path d="M14 14h56v68H14z" />
        <path d="M70 14h66v34H70z" />
        <path d="M70 48h66v34H70z" />
      </g>
      <g stroke="#c0392b" strokeWidth="1" strokeDasharray="3 2" fill="none">
        <path d="M14 74h122" />
      </g>
      <text x="40" y="50" fontSize="11" fill="#111113" fontFamily="Georgia, serif" textAnchor="middle">24</text>
      <text x="103" y="34" fontSize="11" fill="#111113" fontFamily="Georgia, serif" textAnchor="middle">25</text>
      <text x="103" y="68" fontSize="11" fill="#111113" fontFamily="Georgia, serif" textAnchor="middle">26</text>
      <text x="128" y="79" fontSize="6" fill="#c0392b" fontFamily="sans-serif" textAnchor="end">E-1</text>
      <circle cx="103" cy="30" r="11" fill="none" stroke="#E03131" strokeWidth="2" />
    </svg>
  );
}

/* Stylized report preview — the "proof" visual in the hero. */
function ReportMock() {
  return (
    <div className="hero-mock" aria-hidden="true">
      <div className="hero-mock-head">
        <span className="hero-mock-dot" />
        <div>
          <div className="hero-mock-address">123 Smith Street</div>
          <div className="hero-mock-sub">Section 32 · 53 pages · analysed in 1m 48s</div>
        </div>
      </div>

      <div className="hero-mock-risks">
        <span className="hero-mock-chip hero-mock-chip-high">1 High risk</span>
        <span className="hero-mock-chip hero-mock-chip-med">2 Medium</span>
        <span className="hero-mock-chip hero-mock-chip-low">3 Low</span>
      </div>

      <div className="hero-mock-row">
        <div className="hero-mock-plan">
          <MiniPlan />
          <span>Your lot, on the actual plan</span>
        </div>
        <ul className="hero-mock-findings">
          <li><strong>Drainage easement</strong> along the rear boundary</li>
          <li><strong>$4,102/yr</strong> in rates, water &amp; levies</li>
          <li><strong>5 questions</strong> to ask before you offer</li>
        </ul>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: I.flag, title: 'Risks, triaged', text: 'Caveats, covenants, unapproved works and notices — flagged HIGH, MEDIUM or LOW and explained in plain English, not legalese.' },
  { icon: I.map, title: 'Your lot, circled on the real plan', text: 'We find the plan of subdivision inside the document, verify it visually, and circle your lot — easements marked where they actually run.' },
  { icon: I.dollars, title: 'Every fee, totalled', text: 'Council rates, water, owners corporation levies and land tax pulled from the notices and summed — the true annual cost of holding the property.' },
  { icon: I.chat, title: 'Questions for the agent', text: 'Five sharp questions ranked by what matters most in this document, so you walk into the inspection knowing what to ask.' },
  { icon: I.clock, title: 'Minutes, not days', text: 'A 100-page vendor statement is read cover to cover in about two minutes — before the open ends, not after the auction.' },
  { icon: I.shield, title: 'Private by default', text: 'Documents are processed securely and deleted after analysis. No account needed — your reports stay on your device.' },
];

const AUDIENCES = [
  {
    icon: I.home, title: 'First-home buyers',
    text: 'Your first Section 32 is 80+ pages of law you’ve never seen. Get a clear picture of what you’re signing before you talk to anyone.',
    stat: '80+', statLabel: 'pages of legalese, decoded',
  },
  {
    icon: I.gavel, title: 'Auction bidders',
    text: 'The contract goes unconditional the moment the hammer falls. Check the document days before — not in the car outside the auction.',
    stat: '0', statLabel: 'cooling-off once the hammer falls',
  },
  {
    icon: I.trend, title: 'Investors & upgraders',
    text: 'Screening several properties a month? Triage the paperwork in minutes and spend your conveyancer budget on the one you’ll actually buy.',
    stat: '5×', statLabel: 'more contracts screened, same budget',
  },
];

const STEPS = [
  { icon: I.upload, num: '01', title: 'Upload the Section 32/Contract of Sale', text: 'Drag in the PDF from your agent — up to 250MB, scanned or digital. No account, no setup.' },
  { icon: I.scan, num: '02', title: 'Every page gets read', text: 'AI reads the title, plan, certificates and notices — including scanned pages — and cross-checks the plan diagram visually.' },
  { icon: I.report, num: '03', title: 'Get your report', text: 'Risks, easements on the plan, fees totalled and agent questions — in about two minutes, on any device.' },
];

// Illustrative report excerpts — the kinds of findings a report surfaces,
// not customer quotes. Do not present invented people as real customers.
const EXAMPLE_FINDINGS = [
  {
    icon: I.map,
    quote: 'HIGH RISK — A sewerage easement runs along the rear boundary of the lot. Any extension or shed over the easement will need build-over consent from the water authority.',
    name: 'Easements, on the actual plan',
    role: 'From the risks section of a report',
  },
  {
    icon: I.dollars,
    quote: 'Owners corporation levies of $4,820/yr, plus a special levy approved for building works. Combined with rates and water, the true annual holding cost is well above the advertised figure.',
    name: 'Every fee, totalled',
    role: 'From the fees table of a report',
  },
  {
    icon: I.chat,
    quote: 'Ask the agent: why does special condition 7 shorten the standard cooling-off period, and has the vendor obtained the owner-builder warranty insurance referred to in the contract?',
    name: 'Questions worth asking',
    role: 'From the agent questions of a report',
  },
];

const FAQS = [
  {
    q: 'What documents does it work with?',
    a: 'Victorian Section 32 vendor statements (and contracts of sale that include them) as PDF — digital or scanned, up to 250MB. Old scanned plans and certificates are read visually.',
  },
  {
    q: 'Is this legal advice?',
    a: 'No. Prop Doc is an automated analysis tool that helps you understand a document quickly. Always engage a qualified conveyancer or solicitor before signing or bidding — a good report makes that conversation faster and sharper.',
  },
  {
    q: 'What happens to my document?',
    a: 'It’s uploaded over an encrypted connection, analysed, and deleted from the server. Your reports are stored only in your browser — we don’t keep copies. Pro subscribers can sign in by email on any device to use their plan and monthly report count there.',
  },
  {
    q: 'How much does it cost?',
    a: 'A single report is $2.99. If you’re looking at multiple properties, Pro is $19.99/month for 20 reports a month, or $180/year for 200+ reports. You only pay once the analysis is ready.',
  },
  {
    q: 'How accurate is it?',
    a: 'Every page of the document is read, and the plan diagram is verified visually before it’s shown. But no automated tool is perfect — treat the report as a map of what to check, and confirm the details that matter with your conveyancer.',
  },
];

/* Mouse-tracking spotlight: cards get an orange radial glow that follows the
   pointer (CSS reads --mx/--my). Skipped entirely on touch devices. */
function useCardSpotlight() {
  useEffect(() => {
    if (window.matchMedia('(hover: none)').matches) return undefined;
    const cards = document.querySelectorAll('.home .audience-card, .home .feature-card, .home .step-card');
    const move = (e) => {
      const r = e.currentTarget.getBoundingClientRect();
      e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
      e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
    };
    cards.forEach(c => c.addEventListener('mousemove', move));
    return () => cards.forEach(c => c.removeEventListener('mousemove', move));
  }, []);
}

/* Scroll-reveal: elements tagged data-reveal rise in as they enter the
   viewport. Under prefers-reduced-motion the CSS never hides them, so the
   observer is simply unnecessary there. */
function useRevealOnScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const els = document.querySelectorAll('.home [data-reveal]');
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          io.unobserve(entry.target);
        }
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

export default function Home() {
  useRevealOnScroll();
  useCardSpotlight();
  return (
    <main className="home">
      <PageMeta
        path="/"
        description="Prop Doc reads your Victorian Section 32 in minutes — risks in plain English, easements on the actual plan, every fee totalled, and the questions to ask the agent."
      />
      {/* ===== Hero ===== */}
      <section className="hero">
        <div className="hero-bg" aria-hidden="true">
          <div className="hero-plan-lines" />
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />
        </div>
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="hero-eyebrow">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
              </svg>
              Built for Victorian Section 32/Contract of Sale documents
            </span>
            <h1>
              Know exactly what you're buying — <RotatingWord /> you sign.
            </h1>
            <p className="hero-sub">
              Upload the Section 32/Contract of Sale and get a plain-English report in minutes:
              hidden risks, easements drawn on the actual plan, every fee totalled,
              and the questions to ask the agent.
            </p>
            <div className="hero-ctas">
              <Link to="/analyze" className="btn-cta-primary">
                Analyse my Section 32/Contract of Sale {I.arrow}
              </Link>
              <Link to="/pricing" className="btn-cta-ghost">See pricing</Link>
            </div>
            <p className="hero-trust">
              $2.99 per report · No account needed · Documents deleted after analysis
            </p>
          </div>
          <ReportMock />
        </div>
      </section>

      {/* ===== Stats strip ===== */}
      <section className="stats-strip" aria-label="Key numbers">
        <div className="stats-inner">
          <div className="stat"><strong>~2 min</strong><span>average analysis time</span></div>
          <div className="stat"><strong>100+ pages</strong><span>read cover to cover</span></div>
          <div className="stat"><strong>4 fee types</strong><span>found &amp; totalled</span></div>
          <div className="stat"><strong>$2.99</strong><span>per full report</span></div>
        </div>
      </section>

      {/* ===== Who it's for ===== */}
      <section className="section">
        <div className="section-inner">
          <h2 className="section-title" data-reveal>
            The document everyone signs.<br />
            <span className="title-grad">Almost nobody reads.</span>
          </h2>
          <p className="section-sub" data-reveal>
            A Section 32 discloses everything that matters about a property — and buries it
            in 80 pages of certificates. Prop Doc reads it the way a careful buyer wishes they could.
          </p>
          <div className="audience-grid">
            {AUDIENCES.map((a, i) => (
              <div className="audience-card" key={a.title} data-reveal style={{ transitionDelay: `${i * 90}ms` }}>
                <span className="card-icon">{a.icon}</span>
                <h3>{a.title}</h3>
                <p>{a.text}</p>
                <div className="audience-stat" aria-hidden="true">
                  <strong>{a.stat}</strong>
                  <span>{a.statLabel}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== What you get ===== */}
      <section className="section section-alt" id="what-you-get">
        <div className="section-inner">
          <h2 className="section-title" data-reveal>What's in every report</h2>
          <p className="section-sub" data-reveal>
            Not a summary — a working document you can take to the inspection,
            the agent and your conveyancer.
          </p>
          <div className="feature-grid">
            {/* Bento layout: risks card with live-looking chips, a wide card
                showing the actual plan diagram, a gradient fee figure, and a
                full-width privacy banner. */}
            <div className="feature-card" data-reveal>
              <span className="card-icon">{FEATURES[0].icon}</span>
              <h3>{FEATURES[0].title}</h3>
              <p>{FEATURES[0].text}</p>
              <div className="feature-chips" aria-hidden="true">
                <span className="hero-mock-chip hero-mock-chip-high">HIGH</span>
                <span className="hero-mock-chip hero-mock-chip-med">MEDIUM</span>
                <span className="hero-mock-chip hero-mock-chip-low">LOW</span>
              </div>
            </div>

            <div className="feature-card feature-card-wide" data-reveal style={{ transitionDelay: '90ms' }}>
              <div className="feature-card-copy">
                <span className="card-icon">{FEATURES[1].icon}</span>
                <h3>{FEATURES[1].title}</h3>
                <p>{FEATURES[1].text}</p>
              </div>
              <div className="feature-card-visual" aria-hidden="true">
                <MiniPlan />
              </div>
            </div>

            <div className="feature-card" data-reveal>
              <span className="card-icon">{FEATURES[2].icon}</span>
              <h3>{FEATURES[2].title}</h3>
              <p>{FEATURES[2].text}</p>
              <div className="feature-figure" aria-hidden="true">$4,102<span>/yr found</span></div>
            </div>

            <div className="feature-card" data-reveal style={{ transitionDelay: '90ms' }}>
              <span className="card-icon">{FEATURES[3].icon}</span>
              <h3>{FEATURES[3].title}</h3>
              <p>{FEATURES[3].text}</p>
            </div>

            <div className="feature-card" data-reveal style={{ transitionDelay: '180ms' }}>
              <span className="card-icon">{FEATURES[4].icon}</span>
              <h3>{FEATURES[4].title}</h3>
              <p>{FEATURES[4].text}</p>
            </div>

            <div className="feature-card feature-card-banner" data-reveal>
              <span className="card-icon">{FEATURES[5].icon}</span>
              <div>
                <h3>{FEATURES[5].title}</h3>
                <p>{FEATURES[5].text}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="section" id="how-it-works">
        <div className="section-inner">
          <h2 className="section-title" data-reveal>From PDF to clarity in three steps</h2>
          <div className="steps-grid">
            {STEPS.map((s, i) => (
              <div className="step-card" key={s.num} data-reveal style={{ transitionDelay: `${i * 90}ms` }}>
                <div className="step-top">
                  <span className="card-icon">{s.icon}</span>
                  <span className="step-num">{s.num}</span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Example findings ===== */}
      <section className="section section-dark">
        <div className="section-inner">
          <h2 className="section-title" data-reveal>The fine print, surfaced</h2>
          <p className="section-sub" data-reveal>
            Illustrative excerpts of what a Prop Doc report pulls out of a vendor statement.
          </p>
          <div className="quote-grid">
            {EXAMPLE_FINDINGS.map((t, i) => (
              <figure className="quote-card" key={t.name} data-reveal style={{ transitionDelay: `${i * 90}ms` }}>
                <blockquote>“{t.quote}”</blockquote>
                <figcaption>
                  <span className="quote-avatar">{t.icon}</span>
                  <span>
                    <span className="quote-name">{t.name}</span>
                    <span className="quote-role">{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Pricing teaser ===== */}
      <section className="section">
        <div className="section-inner">
          <h2 className="section-title" data-reveal>Simple pricing</h2>
          <p className="section-sub" data-reveal>Pay per report, or go Pro while you're in the market.</p>
          <div className="price-teaser-grid">
            <div className="price-teaser" data-reveal>
              <h3>Single report</h3>
              <div className="price-teaser-amount">$2.99</div>
              <p>One document, full report. Pay only when the analysis is ready.</p>
              <Link to="/pricing" className="price-teaser-cta">Get one report</Link>
            </div>
            <div className="price-teaser price-teaser-hot" data-reveal style={{ transitionDelay: '90ms' }}>
              <span className="price-teaser-badge">Most popular</span>
              <h3>Pro monthly</h3>
              <div className="price-teaser-amount">$19.99<span>/mo</span></div>
              <p>20 reports a month while you're house-hunting. Cancel anytime.</p>
              <Link to="/pricing" className="price-teaser-cta price-teaser-cta-hot">
                Go Pro {I.arrow}
              </Link>
            </div>
            <div className="price-teaser" data-reveal style={{ transitionDelay: '180ms' }}>
              <h3>Pro annual</h3>
              <div className="price-teaser-amount">$180<span>/yr</span></div>
              <p>200+ reports a year for investors and professionals — save 25%.</p>
              <Link to="/pricing" className="price-teaser-cta">Go annual</Link>
            </div>
          </div>
          <div className="section-cta">
            <Link to="/pricing" className="btn-cta-ghost">Compare plans {I.arrow}</Link>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="section section-alt" id="faq">
        <div className="section-inner section-inner-narrow">
          <h2 className="section-title" data-reveal>Questions, answered</h2>
          <div className="faq-list">
            {FAQS.map((f, i) => (
              <details className="faq-item" key={f.q} data-reveal style={{ transitionDelay: `${i * 60}ms` }}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="final-cta">
        <div className="final-cta-inner" data-reveal>
          <h2>The biggest purchase of your life deserves a second read.</h2>
          <p>Upload the Section 32/Contract of Sale now — your report is about two minutes away.</p>
          <Link to="/analyze" className="btn-cta-light">
            Analyse my Section 32/Contract of Sale {I.arrow}
          </Link>
        </div>
      </section>
    </main>
  );
}
